// JanetObjectContext — Janet's persistent object/path (Intentional Propulsion
// Slice 6, issue #169). Tracks the current object Aaron and Janet are
// discussing (Property → Deal / Expression / Person / Artifact…) as a small
// stack of { type, id, label, path } references — reducer logic lives in
// src/lib/janetObjectPath.js, pure and unit-tested. No second truth store:
// this holds ONLY references, never a copy of canonical data, and every
// path is a real route into the canonical domain surface that owns it.
//
// Usage:
//   A canonical detail page announces itself once loaded:
//     const { visitObject } = useJanetObject()
//     useEffect(() => {
//       if (!property) return
//       visitObject({ type: 'property', id: property.id, label: property.title, path: `/properties/${property.id}` })
//     }, [property?.id])
//
//   A lateral door pushes BEFORE navigating away, so the destination's own
//   visitObject() finds itself already current (idempotent — see
//   reduceVisit):
//     pushObject({ type: 'deal', id: deal.id, label: deal.title, path: `/deals/${deal.id}` })
//     zoomNavigate(navigate, `/deals/${deal.id}`)
//
//   "Go back to the project" — anything can call:
//     const target = returnTarget
//     returnObject(); zoomNavigate(navigate, target.path)
//
// Session-scoped (sessionStorage, like zoomMemory.js) and time-bounded
// (janetObjectPath.MAX_AGE_MS) so a stale trail from a prior session never
// leaks back in, and ordinary browsing to an unrelated object resets the
// path rather than growing it forever (reduceVisit's "fresh anchor" case).
//
// A web doorway that opens a companion surface (the Console) in a new tab
// via window.open(..., 'noopener') starts a browsing context with NO
// inherited sessionStorage — this tab's stack never reaches it. For that one
// case, consoleBridge.js's openConsolePath rides the return-to ref through
// the URL instead (janetObjectPath.js's withCarryParam); this provider reads
// + strips it once on mount and holds it as `carriedReturnTo`, a fallback
// source for `returnTarget` alongside the ordinary stack-derived one — same
// field, same JanetReturnBridge consumer, no second mechanism.

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import {
  reduceVisit, reducePush, reducePop, currentOf, returnTargetOf, isFresh,
  CARRY_PARAM, decodeCarryChain,
} from '../lib/janetObjectPath'
import { IS_DEMO } from '../lib/demoClient'

const STORE_KEY = 'chatrone-janet-object-path'

function readStored() {
  if (IS_DEMO) return [] // the trailer shares one account across visitors — no cross-visit trail
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    if (!raw) return []
    const { stack, ts } = JSON.parse(raw)
    if (!isFresh(ts)) return []
    return Array.isArray(stack) ? stack : []
  } catch {
    return []
  }
}

function writeStored(stack) {
  if (IS_DEMO) return
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({ stack, ts: Date.now() }))
  } catch { /* storage unavailable — in-memory only for this tab */ }
}

const JanetObjectContext = createContext({
  stack: [],
  current: null,
  returnTarget: null,
  visitObject: () => {},
  pushObject: () => {},
  returnObject: () => {},
  clearObjectPath: () => {},
})

export function JanetObjectProvider({ children }) {
  const [stack, setStack] = useState(readStored)
  // A return-to ref carried in via the URL (see janetObjectPath.js's
  // withCarryParam) — the fallback source for returnTarget when this tab's
  // OWN stack has nothing (a fresh tab opened with 'noopener' boots with no
  // inherited sessionStorage, so readStored() above finds nothing even
  // though the opener tab pushed a frame right before opening it).
  const [carriedReturnTo, setCarriedReturnTo] = useState(null)

  useEffect(() => { writeStored(stack) }, [stack])

  // Consume the carry param once, on mount — this provider mounts once per
  // tab/app instance (App.jsx), so there's nothing to re-run on route
  // changes. Never for the demo: a shared trailer account must not let one
  // visitor's carried link leak an in-progress bridge to another.
  //
  // The carried payload is a bounded CHAIN (ancestor(s) + the immediate
  // bridged-from object, last) — see janetObjectPath.js's buildCarryChain.
  // Only the ANCESTOR PREFIX (everything but the last element) is seeded
  // into this tab's real `stack`; the immediate object itself stays in
  // `carriedReturnTo` exactly as before, so the doorway's own companion page
  // (the Console — not a canonical OS page, never calls visitObject) still
  // shows "← Back to <immediate>" unchanged. If the user then follows that
  // bridge back to the immediate object's own OS page, its mount-time
  // visitObject() call needs to find itself ALREADY current for the seeded
  // ancestor to survive beneath it (reduceVisit's idempotent case) — that's
  // what returnObject()'s push-instead-of-pop branch below sets up.
  useEffect(() => {
    if (IS_DEMO) return
    try {
      const params = new URLSearchParams(window.location.search)
      const raw = params.get(CARRY_PARAM)
      if (!raw) return
      const chain = decodeCarryChain(raw)
      params.delete(CARRY_PARAM)
      const query = params.toString()
      const clean = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
      window.history.replaceState(window.history.state, '', clean)
      if (chain?.length) {
        const immediate = chain[chain.length - 1]
        const ancestors = chain.slice(0, -1)
        if (ancestors.length) setStack((prev) => (prev.length ? prev : ancestors))
        setCarriedReturnTo(immediate)
      }
    } catch { /* no URL/history access — the bridge just won't show in this tab */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visitObject = useCallback((ref) => setStack((prev) => reduceVisit(prev, ref)), [])
  const pushObject = useCallback((ref) => setStack((prev) => reducePush(prev, ref)), [])
  // Returns the frame now on top (after popping) so a caller can navigate to
  // it without a second render round-trip.
  const returnObject = useCallback(() => {
    let landedOn = null
    setStack((prev) => {
      // Driven by a cross-tab CARRIED fallback, not a real frame this tab
      // ever pushed itself (returnTargetOf(prev) is null — nothing in the
      // real stack to pop toward). Synthesize the frame via PUSH rather than
      // POP: this is the first time this tab's own stack learns about the
      // bridged-from object, mirroring the pushObject() the ORIGINAL tab
      // made before opening the door, so the destination page's own
      // idempotent visitObject() preserves the ancestor chain seeded above
      // it instead of resetting to a fresh single-item stack (2026-09-29
      // repair, issue #170).
      if (!returnTargetOf(prev) && carriedReturnTo) {
        const next = reducePush(prev, carriedReturnTo)
        landedOn = currentOf(next)
        return next
      }
      const next = reducePop(prev)
      landedOn = currentOf(next)
      return next
    })
    setCarriedReturnTo(null)
    return landedOn
  }, [carriedReturnTo])
  const clearObjectPath = useCallback(() => { setStack([]); setCarriedReturnTo(null) }, [])

  const value = useMemo(() => ({
    stack,
    current: currentOf(stack),
    returnTarget: returnTargetOf(stack) || carriedReturnTo,
    visitObject,
    pushObject,
    returnObject,
    clearObjectPath,
  }), [stack, carriedReturnTo, visitObject, pushObject, returnObject, clearObjectPath])

  return (
    <JanetObjectContext.Provider value={value}>
      {children}
    </JanetObjectContext.Provider>
  )
}

export function useJanetObject() {
  return useContext(JanetObjectContext)
}
